import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Badge,
  Modal,
  TextInput,
  Button,
  Select,
  Spinner,
  Label,
} from "flowbite-react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  rectIntersection,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  FaGripVertical,
  FaFilter,
  FaMagnifyingGlass,
  FaArrowLeftLong,
  FaWpforms,
} from "react-icons/fa6";
import { Tooltip } from "react-tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HiClock,
  HiPlus,
  HiUser,
  HiPhone,
  HiVideoCamera,
  HiMail,
  HiUserGroup,
  HiRefresh,
  HiMinus,
  HiChatAlt2,
  HiAnnotation,
  HiSearch,
  HiEye,
  HiEyeOff,
  HiChevronDown,
  HiChevronUp,
  HiViewGrid,
  HiViewList,
  HiCheck,
  HiSparkles,
  HiLink,
} from "react-icons/hi";
import { FaExchangeAlt } from "react-icons/fa";
import {
  HiCog6Tooth,
  HiMiniMagnifyingGlassMinus,
  HiMiniMagnifyingGlassPlus,
  HiPencilSquare,
  HiXMark,
} from "react-icons/hi2";
import axios from "../../config/axiosInstance";
import {
  ADMIN,
  CRM,
  CUSTOM_FIELDS,
  LEADS,
  USERS,
} from "../../config/apiConfig";
import AutoCompleteInput from "../inputs/Autocomplete";
import QuickCreateCustomer from "../forms/QuickCreateCustomer";
import TaskModal from "../modals/TaskModal";
import { useApplication } from "../../contexts/ApplicationContext";
import FunnelTemplateModal from "../modals/FunnelTemplateModal";
import ClientQuickFilter from "../inputs/ClientQuickFilter";
import FunnelSelectorPro from "./FunnelSelectorPro";
import StageInspectorDrawer from "../crm/StageInspectorDrawer";
import FunnelEditorModal from "../modals/FunnelEditorModal";
import AnimatedToast from "../common/AnimatedToast";
import {
  useSocketReady,
  useSocketRef,
} from "../../contexts/SocketContext";

/* ===================== Helpers de moeda / soma por etapa ===================== */
const formatBRL = (n) => {
  const v = Number(n || 0);
  try {
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  } catch {
    return `R$ ${v.toFixed(2)}`.replace(".", ",");
  }
};

const getCardValue = (card) =>
  Number(card?.dealValue ?? card?.value ?? card?.amount ?? 0);

const sumDealValue = (cards = []) =>
  cards.reduce(
    (acc, c) => acc + (Number.isFinite(getCardValue(c)) ? getCardValue(c) : 0),
    0
  );

/* ===== Persistência de filtros no localStorage (por usuário e por funil) ===== */
const FILTERS_KEY = (userId, funnelId) =>
  `crmFilters:${userId || "anon"}:${funnelId || "all"}`;

function loadSavedFilters(userId, funnelId) {
  try {
    const raw = localStorage.getItem(FILTERS_KEY(userId, funnelId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveFilters(userId, funnelId, filters) {
  try {
    localStorage.setItem(
      FILTERS_KEY(userId, funnelId),
      JSON.stringify(filters)
    );
  } catch {}
}

/* ===================== Floating Zoom ===================== */
function FloatingZoomControls({
  value, // número 0.6..1.6
  onIn,
  onOut,
  onReset,
  min = 0.6,
  max = 1.6,
}) {
  const pct = Math.round(value * 100);
  const radius = 15;
  const circ = 2 * Math.PI * radius;
  const clamped = Math.max(min, Math.min(max, value));
  const progress = (clamped - min) / (max - min);
  const dash = Math.max(0.02, Math.min(0.98, progress)) * circ;

  return (
    <>
      <div className="fixed bottom-6 right-[92px] z-50">
        <div
          className={[
            "flex items-center gap-2 rounded-2xl",
            "border border-white/40 bg-white/70 backdrop-blur-md",
            "shadow-md",
            "px-2.5 py-0.5",
          ].join(" ")}
        >
          {/* - */}
          <button
            data-tooltip-id="zoom-float-tip"
            data-tooltip-content="Diminuir (Ctrl/Cmd + '-')"
            onClick={onOut}
            disabled={value <= min}
            className={[
              "h-9 w-9 rounded-xl border transition-all",
              "bg-white hover:bg-gray-50 active:scale-[.98]",
              "border-gray-200 text-gray-700 flex flex-col items-center justify-center",
              value <= min ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <HiMiniMagnifyingGlassMinus />
          </button>

          {/* indicador central */}
          <button
            onClick={onReset}
            data-tooltip-id="zoom-float-tip"
            data-tooltip-content="Resetar (Ctrl/Cmd + '0')"
            className={[
              "relative h-12 w-12 rounded-full",
              "bg-gradient-to-br from-slate-50 to-white",
              "border border-gray-200",
              "shadow-inner hover:shadow transition-all",
            ].join(" ")}
          >
            <span className="relative z-10 text-[10px] font-bold text-gray-800 flex flex-col justify-center items-center">
              {pct}%
            </span>
          </button>

          {/* + */}
          <button
            data-tooltip-id="zoom-float-tip"
            data-tooltip-content="Aumentar (Ctrl/Cmd + '+')"
            onClick={onIn}
            disabled={value >= max}
            className={[
              "h-9 w-9 rounded-xl border transition-all",
              "bg-white hover:bg-gray-50 active:scale-[.98]",
              "border-gray-200 text-gray-700 flex flex-col items-center justify-center",
              value >= max ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
          >
            <HiMiniMagnifyingGlassPlus />
          </button>
        </div>
      </div>
    </>
  );
}

/* ===================== Loader ===================== */
function PageLoader({
  title = "Carregando seu funil...",
  subtitle = "Buscando etapas, cards e filtros",
}) {
  return (
    <div className="w-full flex items-center justify-center">
      <div className="relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="pointer-events-none absolute -top-16 -left-16 h-48 w-48 rounded-full bg-gradient-to-br from-red-200 via-orange-200 to-pink-200 blur-2xl opacity-60" />
        <div className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-gradient-to-br from-blue-200 via-cyan-200 to-teal-200 blur-2xl opacity-60" />
        <div className="relative z-10 p-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
              <Spinner aria-label="Carregando..." size="xl" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
          </div>
          <div className="mt-6 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-orange-200 via-orange-500 to-orange-600 animate-loader-bar" />
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="col-span-1 space-y-2">
              <div className="h-3 w-3/4 rounded bg-gray-100 animate-pulse" />
              <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
            </div>
            <div className="col-span-2 flex gap-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-6 w-16 rounded-full bg-gray-100 animate-pulse"
                />
              ))}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, c) => (
              <div
                key={c}
                className="rounded-2xl border border-gray-200 bg-gray-50 p-3"
              >
                <div className="h-4 w-2/3 rounded bg-gray-200/70 animate-pulse" />
                <div className="mt-3 space-y-2">
                  {[...Array(4)].map((__, r) => (
                    <div
                      key={r}
                      className="h-16 rounded-xl bg-white border border-gray-200 animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes loader-bar {
          0% { transform: translateX(-120%); }
          50% { transform: translateX(20%); }
          100% { transform: translateX(200%); }
        }
        .animate-loader-bar { animation: loader-bar 1.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

/* ===================== Sortable Card (Kanban) ===================== */
function SortableItem({ task, onClick, dimmed = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task?.id });

  const { stats, loading: statsLoading } = useCardTaskStats(task?.id);
  const urgency = getUrgency(stats);
  const color = urgencyColors[urgency];
  const progress =
    stats?.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const dealValue = getCardValue(task);
  const hasDealValue = Number.isFinite(dealValue) && dealValue > 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? "0 8px 20px rgba(0,0,0,.15)" : undefined,
    opacity: isDragging ? 0.75 : dimmed ? 0.5 : 1,
  };

  const nextAction = stats?.nextAction;
  const nextDueStr = nextAction?.dueDate
    ? String(nextAction.dueDate).slice(0, 10)
    : "";
  const { formattedBR, dueColorClass } = formatDue(nextDueStr);
  const NextIcon = actionIconMap[nextAction?.type] || HiRefresh;
  const nextLabel = nextAction
    ? labelMap[nextAction?.type] || "Próxima ação"
    : stats?.total
    ? "Sem próximas ações"
    : "—";

  return (
    <div className="mb-2">
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={[
          "relative rounded-xl border bg-white p-3 transition-all",
          "hover:shadow-md",
          // 👇 não mostra 'grab' no hover; só quando arrastando
          isDragging ? "cursor-grabbing" : "cursor-pointer",
          color.cardBorder,
        ].join(" ")}
        onClick={() => onClick(task)}
        // Evita clique acidental pós-drag
        onClickCapture={(e) => isDragging && e.preventDefault()}
      >
        <div
          className={[
            "absolute left-0 top-0 h-full w-1 rounded-l-xl",
            color.sideStrip,
          ].join(" ")}
        />

        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-gray-800 leading-snug truncate pr-2">
              {task?.lead?.name || "Não atribuído"}
            </h3>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-600">
              <HiUser size={13} className="text-gray-400" />
              <span className="truncate">
                {task?.responsible?.name || "Sem responsável"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {task?.tag && (
              <span
                className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border"
                style={{
                  background: task?.tag?.color,
                  borderColor: task?.tag?.color,
                }}
                title={task?.tag?.name || "Etiqueta"}
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* Ícone apenas visual; drag é no card inteiro */}
            <div
              className="text-gray-300 hover:text-gray-400 pt-0.5"
              onClick={(e) => e.stopPropagation()}
              title="Arrastar"
            >
              <FaGripVertical size={12} />
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <div
            className={[
              "inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border",
              color.badge,
            ].join(" ")}
          >
            <NextIcon size={12} />
            <span className="truncate max-w-[140px]">{nextLabel}</span>
          </div>
          <div className="flex items-center text-[10px]">
            <HiClock size={12} className={`mr-1 ${dueColorClass}`} />
            <span className={dueColorClass}>
              Prazo: {nextDueStr ? formattedBR : "--/--"}
            </span>
          </div>
        </div>

        <div className="mt-2">
          <div className="mt-2 flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200">
                {statsLoading
                  ? "…"
                  : `${stats?.done || 0}/${stats?.total || 0}`}{" "}
                concluídas
              </span>
              {stats?.overdue > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700">
                  {stats.overdue} atrasadas
                </span>
              )}
              {stats?.today > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700">
                  {stats.today} hoje
                </span>
              )}
            </div>

            {hasDealValue && (
              <span
                className="inline-flex items-center max-w-[140px] truncate gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-indigo-50 border-indigo-200 text-indigo-700"
                title={`Valor do negócio: ${formatBRL(dealValue)}`}
                onClick={(e) => e.stopPropagation()}
              >
                {formatBRL(dealValue)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== Helpers / Hooks ===================== */
const actionIconMap = {
  ligacao: HiPhone,
  reuniao: HiVideoCamera,
  email: HiMail,
  visita: HiUserGroup,
  followup: HiRefresh,
  whatsapp: HiChatAlt2,
  nota: HiAnnotation,
};

const urgencyColors = {
  overdue: {
    sideStrip: "bg-gradient-to-b from-red-500 to-red-300",
    progressBar: "bg-red-400",
    badge: "bg-red-50 border-red-200 text-red-700",
    cardBorder: "border-red-100",
  },
  today: {
    sideStrip: "bg-gradient-to-b from-amber-500 to-amber-300",
    progressBar: "bg-amber-400",
    badge: "bg-amber-50 border-amber-200 text-amber-700",
    cardBorder: "border-amber-100",
  },
  upcoming: {
    sideStrip: "bg-gradient-to-b from-emerald-500 to-emerald-300",
    progressBar: "bg-emerald-400",
    badge: "bg-emerald-50 border-emerald-200 text-emerald-700",
    cardBorder: "border-emerald-100",
  },
  none: {
    sideStrip: "bg-gradient-to-b from-gray-300 to-gray-200",
    progressBar: "bg-gray-300",
    badge: "bg-gray-50 border-gray-200 text-gray-600",
    cardBorder: "border-gray-200",
  },
};

function formatDue(dueDateStr) {
  let dueColorClass = "text-gray-400";
  let formattedBR = "";
  if (dueDateStr) {
    const [y, m, d] = dueDateStr.split("-").map(Number);
    const due = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) dueColorClass = "text-red-600";
    else if (diffDays === 0) dueColorClass = "text-amber-600";
    else dueColorClass = "text-emerald-700";
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    formattedBR = `${dd}/${mm}/${y}`;
  }
  return { dueColorClass, formattedBR };
}

function getUrgency(stats) {
  if (!stats || stats.total === 0) return "none";
  if (stats.overdue > 0) return "overdue";
  if (stats.today > 0) return "today";
  if (stats.open > 0) return "upcoming";
  return "none";
}

/* ===== Cache leve para stats ===== */
const _statsCache = new Map();
const statsBus =
  typeof window !== "undefined" && window?.__CRM_STATS_BUS__
    ? window.__CRM_STATS_BUS__
    : new EventTarget();
if (typeof window !== "undefined") window.__CRM_STATS_BUS__ = statsBus;

function useCardTaskStats(cardId) {
  const [loading, setLoading] = React.useState(false);
  const [stats, setStats] = React.useState({
    total: 0,
    open: 0,
    done: 0,
    overdue: 0,
    today: 0,
    nextAction: null,
  });

  const fetchStats = React.useCallback(async () => {
    if (!cardId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${CRM.ADD_CARD_HISTORY}/${cardId}/tasks`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      let total = list.length,
        done = 0,
        overdue = 0,
        today = 0;
      const openItems = [];

      for (const t of list) {
        const isDone = t.status === "done" || !!t.completedAt;
        if (isDone) done += 1;
        else openItems.push(t);

        const dStr = t?.dueDate ? String(t.dueDate).slice(0, 10) : null;
        if (dStr && !isDone) {
          const [y, m, d] = dStr.split("-").map(Number);
          const dd = new Date(y, m - 1, d);
          dd.setHours(0, 0, 0, 0);
          const diff = Math.round((dd - now) / (1000 * 60 * 60 * 24));
          if (diff < 0) overdue += 1;
          else if (diff === 0) today += 1;
        }
      }

      const nextOpen = [...openItems].sort((a, b) => {
        const aKey = a?.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bKey = b?.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aKey - bKey;
      })[0];

      const computed = {
        total,
        open: total - done,
        done,
        overdue,
        today,
        nextAction: nextOpen
          ? { type: nextOpen.type, dueDate: nextOpen.dueDate || null }
          : null,
      };

      setStats(computed);
      _statsCache.set(String(cardId), computed);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  React.useEffect(() => {
    const key = String(cardId || "");
    if (!key) return;
    const cached = _statsCache.get(key);
    if (cached) setStats(cached);
    else fetchStats();
  }, [cardId, fetchStats]);

  React.useEffect(() => {
    if (!cardId) return;
    const handler = (ev) => {
      const target = ev?.detail?.cardId;
      if (target === "*" || target === String(cardId)) {
        _statsCache.delete(String(cardId));
        fetchStats();
      }
    };
    statsBus.addEventListener("invalidate", handler);
    return () => statsBus.removeEventListener("invalidate", handler);
  }, [cardId, fetchStats]);

  return { stats, loading };
}

/* ===================== Column (Kanban) ===================== */
function KanbanColumn({
  title,
  columnId,
  tasks,
  onCardClick,
  isClosing = false,
  color = "black",
  // NOVOS callbacks
  onStageRename, // (stageId, newName) => Promise|void
  onOpenStageSettings, // (stageId) => void
  user,
  canManageFunnels = false,
}) {
  const [scope, setScope] = useState("ALL");
  const { isOver, setNodeRef } = useDroppable({ id: columnId });
  const sortedTasks = tasks || [];
  const stageTotal = useMemo(() => sumDealValue(sortedTasks), [sortedTasks]);

  // ===== Inline rename state =====
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(title || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // se o título mudar externo, sincroniza o draft
    setNameDraft(title || "");
  }, [title]);

  useEffect(() => {
    if (editing) {
      // foco automático ao entrar em edição
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [editing]);

  useEffect(() => {
    if (!canManageFunnels && editing) {
      setEditing(false);
      setSaving(false);
      setNameDraft(title || "");
    }
  }, [canManageFunnels, editing, title]);

  const beginEdit = () => {
    if (!canManageFunnels) return;
    setNameDraft(title || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setNameDraft(title || "");
    setEditing(false);
    setSaving(false);
  };

  const commitEdit = async () => {
    if (!canManageFunnels) {
      cancelEdit();
      return;
    }
    const trimmed = String(nameDraft || "").trim();
    if (!trimmed || trimmed === title) {
      cancelEdit();
      return;
    }
    try {
      setSaving(true);
      await onStageRename?.(columnId, trimmed); // callback do pai
      setEditing(false);
    } catch (e) {
      // opcional: toast de erro
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={`w-[320px] min-w-[320px] p-4 rounded-2xl shadow-md border flex flex-col h-full transition-colors duration-200 ${
        isOver ? "bg-blue-50 border-blue-400" : "bg-white border-gray-200"
      }`}
    >
      {/* HEADER */}
      <div
        className="flex items-center justify-between mb-3 pb-2 gap-2 shrink-0"
        style={{ borderBottom: `1px solid rgb(187, 187, 187)` }}
      >
        {/* Título + edição inline */}
        <div className="flex items-center gap-2 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <input
                ref={inputRef}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={commitEdit}
                disabled={saving}
                className="min-w-0 w-[170px] truncate bg-white border border-gray-300 focus:border-gray-400 focus:ring-0 rounded px-2 py-1 text-[13px] font-semibold text-gray-800"
              />
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitEdit}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                title="Salvar"
                disabled={saving}
              >
                <HiCheck className="h-4 w-4" />
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelEdit}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                title="Cancelar"
                disabled={saving}
              >
                <HiXMark className="h-4 w-4" />
              </button>
            </div>
          ) : canManageFunnels ? (
            <button
              type="button"
              onClick={beginEdit}
              onDoubleClick={beginEdit}
              className="group inline-flex items-center gap-1.5 max-w-[200px]"
              title="Clique para renomear"
            >
              <h3
                className="text-[12px] uppercase font-bold tracking-wide truncate"
                style={{ color: color }}
              >
                {title}
              </h3>
              <HiPencilSquare className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : (
            <h3
              className="text-[12px] uppercase font-bold tracking-wide truncate"
              style={{ color: color }}
            >
              {title}
            </h3>
          )}
        </div>

        {/* contador + engrenagem */}
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-medium text-white rounded-full px-2 py-0.5"
            style={{ background: color || "#000" }}
            title="Quantidade de cards"
          >
            {sortedTasks.length}
          </span>
          {(user?.permissionId === "POWER_USER" ||
            user?.permissionId === "ADMIN") && (
            <button
              type="button"
              onClick={() => onOpenStageSettings?.(columnId)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              title="Configurar etapa"
            >
              <HiCog6Tooth className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* TOTAL DA ETAPA */}
      {sortedTasks.length > 0 && (
        <span
          className="inline-flex mb-2 items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold"
          title={`Total da etapa: ${formatBRL(stageTotal)}`}
        >
          Total da etapa: {formatBRL(stageTotal)}
        </span>
      )}

      {/* LISTA */}
      <div className="flex-1 overflow-y-auto overscroll-contain pr-1">
        <SortableContext
          items={sortedTasks.map((task) => task?.id)}
          strategy={verticalListSortingStrategy}
        >
          {sortedTasks.length === 0 ? (
            <div className="flex items-center justify-center text-sm text-gray-400 italic h-full">
              Arraste uma tarefa aqui
            </div>
          ) : (
            <div className="space-y-3">
              {sortedTasks.map((task) => (
                <SortableItem
                  key={task?.id}
                  task={task}
                  onClick={onCardClick}
                  dimmed={isClosing}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}

/* ===================== Labels ===================== */
const labelMap = {
  ligacao: "Ligação",
  reuniao: "Reunião",
  email: "E-mail",
  visita: "Visita",
  followup: "Follow-up",
  whatsapp: "WhatsApp",
  nota: "Nota",
};

/* ===================== Funções utilitárias adicionais p/ novas views ===================== */

function flattenCards(funnel, filterFn) {
  const rows = [];
  (funnel?.stages || []).forEach((st) => {
    (st.cards || []).forEach((card) => {
      if (!filterFn || filterFn(card)) {
        rows.push({ stage: st, card });
      }
    });
  });
  return rows;
}
function groupByResponsible(funnel, filterFn) {
  const map = new Map(); // respId -> { responsible, cards: [] }
  (funnel?.stages || []).forEach((st) => {
    (st.cards || []).forEach((card) => {
      if (filterFn && !filterFn(card)) return;
      const rid = String(card?.responsible?.id || "sem");
      if (!map.has(rid)) {
        map.set(rid, { responsible: card?.responsible || null, cards: [] });
      }
      map.get(rid).cards.push({ stage: st, card });
    });
  });
  return map;
}

/* ===================== Botão Flutuante (Novo Card) ===================== */
function FloatingAddButton({ onClick }) {
  return (
    <>
      <button
        onClick={onClick}
        data-tooltip-id="add-card-tooltip"
        data-tooltip-content="Adicionar novo card"
        className={[
          "fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full",
          "bg-gradient-to-br from-rose-500 to-red-600",
          "shadow-md",
          "text-white flex items-center justify-center",
          "hover:brightness-110 active:scale-[.98] transition-all",
        ].join(" ")}
      >
        <svg width="26" height="26" viewBox="0 0 24 24">
          <path fill="currentColor" d="M11 5h2v14h-2zM5 11h14v2H5z" />
        </svg>
      </button>
    </>
  );
}

/* ===================== LIST VIEW (tabela compacta) ===================== */
function ListView({ funnel, cardMatchesFilters, onOpenCard }) {
  const rows = useMemo(
    () =>
      flattenCards(funnel, cardMatchesFilters).sort((a, b) => {
        // ordenar por etapa (order), depois prioridade
        const aStage = a.stage?.order ?? 9999;
        const bStage = b.stage?.order ?? 9999;
        if (aStage !== bStage) return aStage - bStage;
        return (a.card.priority ?? 0) - (b.card.priority ?? 0);
      }),
    [funnel, cardMatchesFilters]
  );

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-gray-600">
              <th className="px-3 py-2 font-semibold">Cliente</th>
              <th className="px-3 py-2 font-semibold">Responsável</th>
              <th className="px-3 py-2 font-semibold">Etapa</th>
              <th className="px-3 py-2 font-semibold">Valor</th>
              <th className="px-3 py-2 font-semibold">Tag</th>
              <th className="px-3 py-2 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Nenhum card encontrado com os filtros atuais.
                </td>
              </tr>
            ) : (
              rows.map(({ stage, card }) => (
                <tr
                  key={card.id}
                  className="border-b border-gray-100 hover:bg-gray-50/60"
                >
                  <td className="px-3 py-2 text-gray-800">
                    {card?.lead?.name || "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {card?.responsible?.name || "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{stage?.name}</td>
                  <td className="px-3 py-2 font-semibold text-gray-900">
                    {formatBRL(getCardValue(card))}
                  </td>
                  <td className="px-3 py-2">
                    {card?.tag?.name ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
                        style={{
                          background: "#000",
                          borderColor: card?.tag?.color || "#e5e7eb",
                          color: "#fff",
                        }}
                      >
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ background: card?.tag?.color }}
                        />
                        {card.tag.name}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Button size="xs" onClick={() => onOpenCard(card.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===================== RESPONSIBLE VIEW (colunas por responsável) ===================== */
function ResponsibleView({ funnel, cardMatchesFilters, onOpenCard }) {
  const groups = useMemo(
    () => groupByResponsible(funnel, cardMatchesFilters),
    [funnel, cardMatchesFilters]
  );

  const entries = useMemo(() => {
    return Array.from(groups.entries()).map(([key, val]) => {
      const total = sumDealValue(val.cards.map((r) => r.card));
      return { key, ...val, total };
    });
  }, [groups]);

  if (entries.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-600">
        Nenhum card encontrado com os filtros atuais.
      </div>
    );
  }

  return (
    <div className="mt-4 h-[calc(100vh-190px)] rounded-xl bg-gray-200 overflow-x-auto overflow-y-hidden">
      <div className="inline-flex gap-4 pb-4 h-full p-3">
        {entries
          .sort((a, b) =>
            String(a?.responsible?.name || "Sem responsável").localeCompare(
              String(b?.responsible?.name || "Sem responsável")
            )
          )
          .map((grp) => (
            <div
              key={grp.key}
              className="w-[320px] min-w-[320px] p-4 rounded-2xl shadow-md border bg-white border-gray-200 flex flex-col h-full"
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b gap-2 shrink-0">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase text-gray-500">
                    Responsável
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 truncate">
                    {grp?.responsible?.name || "Sem responsável"}
                  </h3>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-gray-500">Cards</div>
                  <div className="text-xs font-semibold text-gray-800 text-right">
                    {grp.cards.length}
                  </div>
                </div>
              </div>

              <span
                className="inline-flex mb-2 items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-2 py-0.5 text-[11px] font-semibold"
                title={`Total: ${formatBRL(grp.total)}`}
              >
                Total: {formatBRL(grp.total)}
              </span>

              <div className="flex-1 overflow-y-auto pr-1">
                <div className="space-y-3">
                  {grp.cards
                    .sort(
                      (a, b) => (a.card.priority ?? 0) - (b.card.priority ?? 0)
                    )
                    .map(({ stage, card }) => (
                      <div
                        key={card.id}
                        className="rounded-xl border bg-white p-3 hover:shadow-sm transition cursor-pointer"
                        onClick={() => onOpenCard(card.id)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-[13px] font-semibold text-gray-800 truncate pr-2">
                            {card?.lead?.name || "—"}
                          </h4>
                          {card?.tag && (
                            <span
                              className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border"
                              style={{
                                background: card?.tag?.color,
                                borderColor: card?.tag?.color,
                              }}
                              title={card?.tag?.name || "Etiqueta"}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                        <div className="mt-1 flex items-center justify-between text-[11px] text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Badge color="gray" size="xs">
                              {stage?.name || "Etapa"}
                            </Badge>
                          </span>
                          <span className="font-semibold text-indigo-700">
                            {formatBRL(getCardValue(card))}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ===================== CRMTable (com 3 visualizações) ===================== */
export default function CRMTable() {
  const { user } = useApplication();
  const location = useLocation();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const navigate = useNavigate();
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [publicFormLink, setPublicFormLink] = useState("");
  const [publicFormLoading, setPublicFormLoading] = useState(false);
  const [copyPublicFormFeedback, setCopyPublicFormFeedback] = useState("");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [funnelEditorOpen, setFunnelEditorOpen] = useState(false);
  const [funnelEditorData, setFunnelEditorData] = useState(null);
  const [isFunnelPickerOpen, setIsFunnelPickerOpen] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [configMenuPos, setConfigMenuPos] = useState({ top: 0, left: 0 });
  const configBtnRef = useRef(null);
  const configMenuRef = useRef(null);

  // Visualização atual
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("crmViewMode") || "kanban";
    } catch {
      return "kanban";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("crmViewMode", viewMode);
    } catch {}
  }, [viewMode]);

  // filtros ativos
  const [filterTagId, setFilterTagId] = useState("");
  // filtros temporários (do modal)
  const [tmpFilterTagId, setTmpFilterTagId] = useState("");

  // ====== DnD State (kanban) ======
  const [activeId, setActiveId] = useState(null);
  const [activeCard, setActiveCard] = useState(null);

  // ====== Zoom (apenas para Kanban) ======
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem("crmBoardZoom");
    const n = Number(saved);
    return Number.isFinite(n) ? Math.min(1.6, Math.max(0.6, n)) : 1;
  });
  const ZMIN = 0.6,
    ZMAX = 1.6,
    ZSTEP = 0.1;
  const setZoomSafely = (val) => {
    const next = Math.round(Math.min(ZMAX, Math.max(ZMIN, val)) * 100) / 100;
    setZoom(next);
    try {
      localStorage.setItem("crmBoardZoom", String(next));
    } catch {}
  };
  const zoomIn = () => setZoomSafely(zoom + ZSTEP);
  const zoomOut = () => setZoomSafely(zoom - ZSTEP);
  const zoomReset = () => setZoomSafely(1);

  const myTeamIds = useMemo(() => {
    const seen = new Set();
    const add = (maybeId) => {
      if (maybeId === null || maybeId === undefined) return;
      const normalized = String(maybeId);
      if (normalized.trim()) {
        seen.add(normalized);
      }
    };
    if (Array.isArray(user?.teamIds)) {
      user.teamIds.forEach(add);
    }
    if (Array.isArray(user?.Teams)) {
      user.Teams.forEach((team) => {
        add(team?.id);
        add(team?.teamId);
        add(team?.team?.id);
      });
    }
    if (Array.isArray(user?.teams)) {
      user.teams.forEach((team) => {
        add(team?.id);
        add(team?.teamId);
        add(team?.team?.id);
      });
    }
    return Array.from(seen);
  }, [user]);

  const userTeamRoles = useMemo(() => {
    const map = new Map();
    const teamsList = Array.isArray(user?.Teams) ? user.Teams : [];
    teamsList.forEach((team) => {
      const teamId =
        team?.id ??
        team?.teamId ??
        (team?.team?.id || team?.team?.teamId) ??
        null;
      const role =
        (team?.TeamUser?.role || team?.role || "")
          .toString()
          .toLowerCase()
          .trim();
      if (teamId && role) {
        map.set(String(teamId), role);
      }
    });
    return map;
  }, [user]);

  const resolveFunnelTeamId = (funnel) => {
    if (!funnel) return null;
    return (
      funnel.teamId ??
      funnel.team?.id ??
      funnel.team?.teamId ??
      funnel.team?.team?.id ??
      null
    );
  };

  const resolveFunnelOwnerId = (funnel) =>
    funnel?.ownerUserId ?? funnel?.owner?.id ?? funnel?.ownerId ?? null;

  const canManageFunnels = useMemo(() => {
    const role = String(user?.permissionId || "").toUpperCase();
    if (role === "ADMIN" || role === "POWER_USER") return true;
    if (!selectedFunnel) return false;
    const teamId = resolveFunnelTeamId(selectedFunnel);
    if (!teamId) return false;
    const memberRole = userTeamRoles.get(String(teamId));
    return memberRole === "admin";
  }, [user?.permissionId, selectedFunnel, userTeamRoles]);

  const canSeeFunnel = (f) => {
    if (!f) return false;
    const vis = String(f.visibility || "").toUpperCase();
    if (vis === "PUBLIC") return true;
    if (vis === "PRIVATE") {
      const ownerId = resolveFunnelOwnerId(f);
      return (
        user?.id &&
        ownerId !== null &&
        String(ownerId) === String(user.id)
      );
    }
    if (vis === "TEAM") {
      const teamId = resolveFunnelTeamId(f);
      return teamId ? myTeamIds.includes(String(teamId)) : false;
    }
    return false;
  };

  const toggleConfigMenu = () => {
    if (!canManageFunnels) return;
    if (!configBtnRef.current) {
      setConfigMenuOpen((prev) => !prev);
      return;
    }

    const rect = configBtnRef.current.getBoundingClientRect();

    setConfigMenuPos({
      top: rect.bottom + 8, // 8px abaixo do botão
      left: rect.left - 150, // alinhado à esquerda do botão
    });

    setConfigMenuOpen((prev) => !prev);
  };

  const openCustomFieldsConfig = () => {
    if (!selectedFunnelId) return;
    // Ajuste essa rota conforme sua estrutura real:
    navigate(`/crm/fields?f=${selectedFunnelId}`);
  };

  useEffect(() => {
    if (!canManageFunnels && configMenuOpen) {
      setConfigMenuOpen(false);
    }
  }, [canManageFunnels, configMenuOpen]);

  useEffect(() => {
    if (!configMenuOpen || !canManageFunnels) return;

    const handleClickOutside = (e) => {
      const btn = configBtnRef.current;
      const menu = configMenuRef.current;

      if (btn && !btn.contains(e.target) && menu && !menu.contains(e.target)) {
        setConfigMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [configMenuOpen, canManageFunnels]);

  // --- URL helpers (logo após os outros useState/useEffect) ---
  const openCardById = async (cardId, { push = true } = {}) => {
    if (!cardId) return;
    await fetchCardDetails(cardId);
    const p = new URLSearchParams(location.search);
    p.set("cardId", String(cardId));
    navigate(
      { pathname: location.pathname, search: p.toString() },
      { replace: !push }
    );
  };

  const closeCardModal = () => {
    const p = new URLSearchParams(location.search);
    p.delete("cardId");
    navigate(
      { pathname: location.pathname, search: p.toString() },
      { replace: true }
    );
    setSelectedTask(null);
  };

  // Quando a URL tiver ?cardId=..., abre o modal automaticamente
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const fromUrl = p.get("cardId");
    if (!fromUrl) return;
    if (!selectedTask || String(selectedTask.id) !== String(fromUrl)) {
      fetchCardDetails(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const tagOptions = useMemo(() => {
    const set = new Map(); // id -> {id, name, color}
    (selectedFunnel?.stages || []).forEach((st) => {
      (st.cards || []).forEach((c) => {
        const t = c?.tag;
        if (t?.id && !set.has(String(t.id))) {
          set.set(String(t.id), { id: t.id, name: t.name, color: t.color });
        }
      });
    });
    return Array.from(set.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  }, [selectedFunnel]);

  useEffect(() => {
    const onKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === "=" || e.key === "+") {
        e.preventDefault();
        zoomIn();
      }
      if (e.key === "-") {
        e.preventDefault();
        zoomOut();
      }
      if (e.key === "0") {
        e.preventDefault();
        zoomReset();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Create card modal
  const [newCardModalOpen, setNewCardModalOpen] = useState(false);
  const [newCardClientId, setNewCardClientId] = useState("");
  const [newCardResponsible, setNewCardResponsible] = useState(user?.id || "");
  const [newCardStageId, setNewCardStageId] = useState("");
  const [creating, setCreating] = useState(false);
  const [cardError, setCardError] = useState("");
  const closeNewCardModal = () => {
    setNewCardModalOpen(false);
    setCardError("");
  };
  // Filters
  // agora é um array de ids de clientes
  const [filterClientId, setFilterClientId] = useState("");
  const [filterClientIds, setFilterClientIds] = useState([]); // ex: ["1","2"]
  const [filterResponsibleId, setFilterResponsibleId] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDue, setFilterDue] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tmpFilterClientId, setTmpFilterClientId] = useState("");

  // estados temporários do modal de filtros
  const [tmpFilterClientIds, setTmpFilterClientIds] = useState([]);
  const [tmpFilterResponsibleId, setTmpFilterResponsibleId] = useState("");
  const [tmpFilterSearch, setTmpFilterSearch] = useState("");
  const [tmpFilterDue, setTmpFilterDue] = useState("");

  const [fields, setFields] = useState([]);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [requiredFieldValues, setRequiredFieldValues] = useState({});
  const [savingRequiredFields, setSavingRequiredFields] = useState(false);
  const [requiredError, setRequiredError] = useState("");
  const [automationToast, setAutomationToast] = useState(null);
  const automationRefreshRef = useRef(null);
  const socketRef = useSocketRef();
  const socketReady = useSocketReady();
  const [dragSourceStageId, setDragSourceStageId] = useState(null);

  const [headerHidden, setHeaderHidden] = useState(() => {
    try {
      return localStorage.getItem("crmHeaderHidden") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("crmHeaderHidden", headerHidden ? "1" : "0");
    } catch {}
  }, [headerHidden]);

  // Details
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskHistory, setTaskHistory] = useState([]);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);

  // Sensores DnD (kanban)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /* ====== Fetch base ====== */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        setAvailableUsers(res.data.data.data || []);
      } catch (err) {
        console.error("Erro ao buscar usuários:", err);
      }
    };
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${LEADS.LIST}?itemsPerPage=-1`);
        setAvailableClients(res.data.data.data || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      }
    };
    fetchUsers();
    fetchClients();
  }, []);

  const fetchFunnels = async () => {
    let hadFunnels = false;
    try {
      setLoading(true);
      const res = await axios.get(`${CRM.FUNNELS}`);
      const all = res.data?.data || [];
      const visible = all.filter(canSeeFunnel);

      setFunnels(visible);
      hadFunnels = visible.length > 0;

      if (hadFunnels) {
        const params = new URLSearchParams(location.search);
        const urlFunnelId = params.get("f");

        const pick =
          visible.find((f) => String(f.id) === String(urlFunnelId)) ||
          visible[0];

        setSelectedFunnelId(pick.id);
        await fetchFunnel(pick.id);
      }
    } catch (err) {
      console.error("Erro ao listar funis", err);
    } finally {
      if (!hadFunnels) setLoading(false);
    }
  };

  const loadPublicFormLink = useCallback(
    async (id) => {
      if (!id) {
        setPublicFormLink("");
        return;
      }
      setPublicFormLoading(true);
      try {
        const res = await axios.get(CRM.FUNNEL_PUBLIC_FORM(id));
        const data = res?.data?.data;
        setPublicFormLink(data?.publicUrl || "");
      } catch (err) {
        setPublicFormLink("");
      } finally {
        setPublicFormLoading(false);
      }
    },
    []
  );

  const fetchFunnel = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${CRM.FUNNEL_DETAILS}/${id}/details`);
      const data = res.data.data;
      const normalized = {
        ...data,
        stages: (data.stages || []).map((st) => {
          const cards = [...(st.cards || [])]
            .sort((a, b) => {
              const pa = Number.isFinite(+a.priority) ? +a.priority : Infinity;
              const pb = Number.isFinite(+b.priority) ? +b.priority : Infinity;
              return pa - pb;
            })
            .map((c, i) => ({
              ...c,
              // se o backend não mandar prioridade, force uma
              priority: Number.isFinite(+c.priority) ? +c.priority : i + 1,
            }));
          return { ...st, cards };
        }),
      };
      setSelectedFunnel(normalized);
      const sts = res?.data?.data?.stages || [];
      if (sts.length) setNewCardStageId(String(sts[0].id));
      else setNewCardStageId("");
      await loadPublicFormLink(id);
    } catch (err) {
      console.error("Erro ao buscar funil", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFields = async () => {
    if (!selectedFunnelId) {
      setFields([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(CUSTOM_FIELDS.LIST(selectedFunnelId));
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      setFields(
        list.sort(
          (a, b) =>
            (a.order ?? 9999) - (b.order ?? 9999) ||
            String(a.label || "").localeCompare(String(b.label || ""))
        )
      );
    } catch (e) {
      console.error("Erro ao listar campos customizados", e);
    } finally {
      setLoading(false);
    }
  };

  const copyPublicFormLink = async () => {
    if (!publicFormLink) return;
    try {
      await navigator.clipboard.writeText(publicFormLink);
      setCopyPublicFormFeedback("Link copiado");
    } catch (err) {
      setCopyPublicFormFeedback("Não foi possível copiar");
    }
    setTimeout(() => setCopyPublicFormFeedback(""), 2500);
  };

  useEffect(() => {
    if (!selectedFunnelId) return;
    fetchFields();
  }, [selectedFunnelId]);

  useEffect(() => {
    if (user) fetchFunnels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedFunnelId) fetchFunnel(selectedFunnelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFunnelId]);

  useEffect(() => {
    return () => {
      if (automationRefreshRef.current) {
        clearTimeout(automationRefreshRef.current);
        automationRefreshRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const s = socketRef.current;
    if (!socketReady || !s) return;

    const onAutomationEvent = (payload) => {
      const funnelId =
        payload?.funnelId ??
        payload?.cardFunnelId ??
        payload?.card?.stage?.funnelId;
      if (
        !funnelId ||
        !selectedFunnelId ||
        String(funnelId) !== String(selectedFunnelId)
      ) {
        return;
      }
      const toastMessage =
        payload?.toastMessage ||
        payload?.message ||
        "Automação executada para este funil.";
      setAutomationToast({
        id: payload?.timestamp || Date.now(),
        message: toastMessage,
      });
    };

    s.on("automation:executed", onAutomationEvent);

    return () => {
      try {
        s.off("automation:executed", onAutomationEvent);
      } catch {}
    };
  }, [selectedFunnelId, socketReady, socketRef]);

  // Sincroniza o selectedFunnelId -> URL (?f=)
  useEffect(() => {
    if (!selectedFunnelId) return;
    const params = new URLSearchParams(location.search);
    if (params.get("f") !== String(selectedFunnelId)) {
      params.set("f", String(selectedFunnelId));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFunnelId]);

  // Ao mudar de funil, restaura filtros salvos para esse funil/usuário
  useEffect(() => {
    if (!selectedFunnelId || !user?.id) return;
    const saved = loadSavedFilters(user.id, selectedFunnelId);
    if (saved) {
      // compat: aceita tanto filterClientIds (novo) quanto filterClientId (antigo)
      const rawClient = saved.filterClientIds ?? saved.filterClientId ?? "";
      const normalizedClientIds = Array.isArray(rawClient)
        ? rawClient.map((v) => String(v))
        : rawClient
        ? [String(rawClient)]
        : [];

      setFilterClientIds(normalizedClientIds);
      setFilterResponsibleId(saved.filterResponsibleId || "");
      setFilterSearch(saved.filterSearch || "");
      setFilterDue(saved.filterDue || "");
      setFilterTagId(saved.filterTagId || "");
    }
  }, [selectedFunnelId, user?.id]);

  // Sempre que filtros mudarem, persistir (por usuário e por funil)
  useEffect(() => {
    if (!selectedFunnelId || !user?.id) return;
    saveFilters(user.id, selectedFunnelId, {
      // novo formato
      filterClientIds,
      filterResponsibleId,
      filterSearch,
      filterDue,
      filterTagId,
    });
  }, [
    filterClientIds,
    filterResponsibleId,
    filterSearch,
    filterDue,
    filterTagId,
    selectedFunnelId,
    user?.id,
  ]);

  /* ====== Local updates ====== */
  const updateStageCards = (stageId, newCards) => {
    setSelectedFunnel((prev) => ({
      ...prev,
      stages: prev.stages.map((stage) =>
        stage.id === stageId ? { ...stage, cards: newCards } : stage
      ),
    }));
  };

  const updateTask = (updatedTask) => {
    const stage = selectedFunnel?.stages?.find((s) =>
      s.cards?.some((c) => c.id === updatedTask.id)
    );
    if (!stage) return;
    const updatedCards = stage.cards.map((c) =>
      c.id === updatedTask.id ? updatedTask : c
    );
    updateStageCards(stage.id, updatedCards);
  };

  const updateCardBackend = async (cardId, data) => {
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${cardId}`, data);
    } catch (err) {
      console.error("Erro ao atualizar card no backend:", err);
    }
  };

  const resolveFieldKey = (field) =>
    field.key || field.fieldKey || field.name || String(field.id);

  const buildCardCustomFieldValuesMap = (card, requiredFields = []) => {
    if (!card) return {};

    // backward compat: se vier um objeto pronto
    if (
      card.customFields &&
      !Array.isArray(card.customFields) &&
      !card.customFieldValues
    ) {
      return card.customFields;
    }

    const values = card.customFieldValues || [];
    const fields = Array.isArray(requiredFields) ? requiredFields : [];

    const map = {};

    values.forEach((cv) => {
      const field =
        cv.field ||
        fields.find((f) => String(f.id) === String(cv.customFieldId));

      if (!field) return;

      const key = resolveFieldKey(field);
      const rawType = (field.type || "text").toLowerCase();
      let raw = null;

      switch (rawType) {
        case "number":
        case "currency": {
          raw = cv?.valueNumber ?? "";
          break;
        }

        case "boolean": {
          const n = cv?.valueText;
          if (n === null || n === undefined) raw = false;
          else if (n === true || n === "true" || n === 1 || n === "1")
            raw = true;
          else raw = false;
          break;
        }

        case "date": {
          raw = cv?.valueDate
            ? String(cv.valueDate).replace("T00:00:00.000Z", "")
            : "";
          break;
        }

        case "datetime": {
          raw = cv?.valueDate || "";
          break;
        }

        case "multi_select": {
          raw = Array.isArray(cv?.valueJson) ? cv.valueJson : [];
          break;
        }

        default: {
          raw = cv?.valueText ?? cv?.valueJson ?? "";
          break;
        }
      }

      if (raw !== undefined) {
        map[key] = raw;
      }
    });

    return map;
  };

  const buildCustomFieldsPayload = (requiredFields, requiredFieldValues) => {
    if (!Array.isArray(requiredFields) || !requiredFields.length) return [];

    return requiredFields.map((field) => {
      const key = resolveFieldKey(field);
      return {
        customFieldId: field.id,
        value: requiredFieldValues[key] ?? "",
      };
    });
  };

  const handleToggleMultiRequiredField = (field, optionValue) => {
    const key = resolveFieldKey(field);
    setRequiredFieldValues((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      if (current.includes(optionValue)) {
        return {
          ...prev,
          [key]: current.filter((v) => v !== optionValue),
        };
      }
      return {
        ...prev,
        [key]: [...current, optionValue],
      };
    });
  };

  /* ====== Collision strategy (Kanban) ====== */
  const collisionDetection = (args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    const rect = rectIntersection(args);
    if (rect.length > 0) return rect;
    return closestCenter(args);
  };

  // Retorna os custom fields obrigatórios para uma etapa específica (ordenados)
  const getRequiredFieldsForStage = (stageId) => {
    if (!selectedFunnel) return [];

    const stage = selectedFunnel.stages?.find(
      (st) => String(st.id) === String(stageId)
    );

    if (!stage) {
      console.warn("[CRM] Stage não encontrado para stageId", stageId);
      return [];
    }

    const rawRequired = Array.isArray(stage.requiredFields)
      ? stage.requiredFields
      : [];

    const requiredEntries = rawRequired
      .map((item, idx) => {
        if (typeof item === "number" || typeof item === "string") {
          return { id: String(item), priority: idx + 1, orderIndex: idx };
        }
        if (item && (item.customFieldId || item.id)) {
          return {
            id: String(item.customFieldId || item.id),
            priority: Number(item.priority) || idx + 1,
            orderIndex: idx,
          };
        }
        return null;
      })
      .filter(Boolean)
      ;

    if (!requiredEntries.length) {
      return [];
    }

    const allFields = Array.isArray(selectedFunnel.customFields)
      ? selectedFunnel.customFields
      : fields || [];

    const requiredFields = requiredEntries
      .map((entry) => {
        const found = allFields.find((f) => String(f.id) === String(entry.id));
        return found
          ? { ...found, priority: entry.priority, orderIndex: entry.orderIndex }
          : null;
      })
      .filter(Boolean);

    return requiredFields;
  };

  /* ====== DnD Handlers (Kanban) ====== */

  const findStageByCardId = (cardId) =>
    selectedFunnel?.stages?.find((s) => s.cards?.some((c) => c.id === cardId));

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    const stage = findStageByCardId(active.id);
    const card = stage?.cards?.find((c) => c.id === active.id);

    setActiveCard(card || null);

    // 👇 guarda a etapa de origem
    setDragSourceStageId(stage ? stage.id : null);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over || !selectedFunnel) return;

    const activeStage = findStageByCardId(active.id);
    const overStage =
      selectedFunnel.stages.find((s) =>
        s.cards.some((c) => c.id === over.id)
      ) || selectedFunnel.stages.find((s) => s.id === over.id);

    if (!activeStage || !overStage) return;

    // mesmo stage → só reordenar visualmente
    if (activeStage.id === overStage.id) {
      const from = activeStage.cards.findIndex((c) => c.id === active.id);
      const to = activeStage.cards.findIndex((c) => c.id === over.id);
      if (from !== to && to !== -1) {
        const reordered = arrayMove(activeStage.cards, from, to).map(
          (c, i) => ({
            ...c,
            priority: i + 1,
          })
        );
        updateStageCards(activeStage.id, reordered);
      }
      return;
    }

    // stage diferente → move visualmente (mas ainda sem persistir)
    const moving = activeStage.cards.find((c) => c.id === active.id);
    if (!moving) return;

    const newSource = activeStage.cards
      .filter((c) => c.id !== active.id)
      .map((c, i) => ({ ...c, priority: i + 1 }));

    const overIndex = overStage.cards.findIndex((c) => c.id === over.id);
    let newTarget;
    if (overIndex === -1) {
      newTarget = [
        { ...moving, stageId: overStage.id, priority: 1 },
        ...overStage.cards,
      ].map((c, i) => ({ ...c, priority: i + 1 }));
    } else {
      const cloned = [...overStage.cards];
      cloned.splice(overIndex, 0, { ...moving, stageId: overStage.id });
      newTarget = cloned.map((c, i) => ({ ...c, priority: i + 1 }));
    }

    updateStageCards(activeStage.id, newSource);
    updateStageCards(overStage.id, newTarget);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveCard(null);
    if (!over || !selectedFunnel) return;

    // 👇 pega a etapa de origem a partir do ID salvo
    const sourceStage = selectedFunnel.stages.find(
      (s) => String(s.id) === String(dragSourceStageId)
    );

    const targetStage =
      selectedFunnel.stages.find((s) =>
        s.cards.some((c) => c.id === over.id)
      ) || selectedFunnel.stages.find((s) => s.id === over.id);

    // limpa o state
    setDragSourceStageId(null);

    if (!sourceStage || !targetStage) return;

    // MESMA ETAPA → só persiste a nova ordem
    if (String(sourceStage.id) === String(targetStage.id)) {
      const updated = (sourceStage.cards || []).map((c, i) => ({
        ...c,
        priority: i + 1,
        stageId: sourceStage.id,
      }));

      try {
        await Promise.all(
          updated.map((card) =>
            updateCardBackend(card.id, {
              stageId: sourceStage.id,
              priority: card.priority,
            })
          )
        );
      } catch (e) {
        console.error("Erro ao reordenar cards:", e);
      }
      return;
    }

    // ETAPA DIFERENTE → checa campos obrigatórios da etapa alvo
    const requiredFields = getRequiredFieldsForStage(targetStage.id);

    if (requiredFields.length > 0) {
      const movingCard =
        targetStage.cards.find((c) => c.id === active.id) ||
        sourceStage.cards.find((c) => c.id === active.id);

      // 🔹 monta valores iniciais a partir do card
      const initialValues = buildCardCustomFieldValuesMap(
        movingCard,
        requiredFields
      );

      setPendingStageChange({
        card: movingCard,
        sourceStageId: sourceStage.id,
        targetStageId: targetStage.id,
        requiredFields,
      });

      setRequiredFieldValues(initialValues); // 👈 já entra preenchido
      setRequiredError("");
      return;
    }

    // NÃO TEM CAMPOS OBRIGATÓRIOS → segue fluxo normal, persiste backend
    const srcUpdated = (sourceStage.cards || []).map((c, i) => ({
      ...c,
      priority: i + 1,
      stageId: sourceStage.id,
    }));
    const tgtUpdated = (targetStage.cards || []).map((c, i) => ({
      ...c,
      priority: i + 1,
      stageId: targetStage.id,
    }));

    try {
      await Promise.all([
        ...srcUpdated.map((card) =>
          updateCardBackend(card.id, {
            stageId: sourceStage.id,
            priority: card.priority,
          })
        ),
        ...tgtUpdated.map((card) =>
          updateCardBackend(card.id, {
            stageId: targetStage.id,
            priority: card.priority,
          })
        ),
      ]);
      await refetchCard(active.id);
    } catch (e) {
      console.error("Erro ao mover card:", e);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveCard(null);
  };

  /* ====== Campos obrigatórios (modal) ====== */

  const handleChangeRequiredField = (field, value) => {
    const key = resolveFieldKey(field);
    setRequiredFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCancelRequiredModal = () => {
    if (!pendingStageChange || !selectedFunnel) {
      setPendingStageChange(null);
      setRequiredFieldValues({});
      setRequiredError("");
      return;
    }

    const { card, sourceStageId, targetStageId } = pendingStageChange;

    const sourceStage = selectedFunnel.stages.find(
      (s) => String(s.id) === String(sourceStageId)
    );
    const targetStage = selectedFunnel.stages.find(
      (s) => String(s.id) === String(targetStageId)
    );

    if (sourceStage && targetStage) {
      const newTargetCards = (targetStage.cards || []).filter(
        (c) => c.id !== card.id
      );
      const newSourceCards = [
        ...(sourceStage.cards || []),
        { ...card, stageId: sourceStage.id },
      ];

      const srcUpdated = newSourceCards.map((c, i) => ({
        ...c,
        priority: i + 1,
      }));
      const tgtUpdated = newTargetCards.map((c, i) => ({
        ...c,
        priority: i + 1,
      }));

      updateStageCards(sourceStage.id, srcUpdated);
      updateStageCards(targetStage.id, tgtUpdated);
    }

    setPendingStageChange(null);
    setRequiredFieldValues({});
    setRequiredError("");
  };

  const handleConfirmStageChange = async () => {
    if (!pendingStageChange || !selectedFunnel) return;

    const { card, sourceStageId, targetStageId, requiredFields } =
      pendingStageChange;

    if (!card || !sourceStageId || !targetStageId) return;

    // 1) valida obrigatórios
    const missing = requiredFields.filter((field) => {
      const key = resolveFieldKey(field);
      const val = requiredFieldValues[key];

      if (Array.isArray(val)) return val.length === 0;
      return val === null || val === undefined || val === "";
    });

    if (missing.length > 0) {
      setRequiredError(
        `Preencha: ${missing
          .map((f) => f.label || f.key || f.name || "campo")
          .join(", ")}.`
      );
      return;
    }

    const sourceStage = selectedFunnel.stages.find(
      (s) => String(s.id) === String(sourceStageId)
    );
    const targetStage = selectedFunnel.stages.find(
      (s) => String(s.id) === String(targetStageId)
    );

    if (!sourceStage || !targetStage) return;

    setSavingRequiredFields(true);

    // 2) recalc prioridades
    const movingCard = card;
    const srcUpdated = (sourceStage.cards || [])
      .filter((c) => String(c.id) !== String(card.id))
      .map((c, i) => ({
        ...c,
        priority: i + 1,
        stageId: sourceStage.id,
      }));

    const tgtUpdated = [
      { ...movingCard, priority: 1, stageId: targetStage.id },
      ...(targetStage.cards || []).map((c, i) => ({
        ...c,
        priority: i + 2,
        stageId: targetStage.id,
      })),
    ];

    // 3) payload de customFields do card que está indo pra etapa nova
    const customFieldsPayload = buildCustomFieldsPayload(
      requiredFields,
      requiredFieldValues
    );

    try {
      await Promise.all([
        // origem
        ...srcUpdated.map((c) =>
          updateCardBackend(c.id, {
            stageId: sourceStage.id,
            priority: c.priority,
          })
        ),
        // destino (inclui customFields no card movido)
        ...tgtUpdated.map((c) => {
          const basePayload = {
            stageId: targetStage.id,
            priority: c.priority,
          };

          if (String(c.id) === String(card.id)) {
            return updateCardBackend(c.id, {
              ...basePayload,
              customFields: customFieldsPayload,
            });
          }

          return updateCardBackend(c.id, basePayload);
        }),
      ]);

      await refetchCard(card.id);

      setPendingStageChange(null);
      setRequiredFieldValues({});
      setRequiredError("");
    } catch (err) {
      console.error("Erro ao confirmar mudança de etapa:", err);
      setRequiredError(
        "Não foi possível salvar os campos obrigatórios. Tente novamente."
      );
    } finally {
      setSavingRequiredFields(false);
    }
  };

  /* ====== Create Card ====== */
  const leadAlreadyInFunnel = (leadId, funnel) => {
    if (!leadId || !funnel) return false;
    const normalizeLeadId = (card) =>
      String(
        card?.leadId ||
          card?.lead?.id ||
          card?.lead?.leadId ||
          card?.client?.id ||
          ""
      );

    const normalizedId = String(leadId);
    const hasCardInStage = (stage) => {
      const cards = Array.isArray(stage?.cards) ? stage.cards : [];
      return cards.some((card) => normalizeLeadId(card) === normalizedId);
    };

    const stages = Array.isArray(funnel?.stages) ? funnel.stages : [];
    return stages.some((stage) => hasCardInStage(stage));
  };

  const handleCreateCard = async () => {
    const targetStage = selectedFunnel?.stages?.find(
      (s) => String(s.id) === String(newCardStageId || "")
    );
    if (!targetStage || !newCardClientId) return;

    const clientEntry = availableClients.find(
      (c) => String(c.id) === String(newCardClientId)
    );
    if (
      clientEntry &&
      selectedFunnel &&
      leadAlreadyInFunnel(newCardClientId, selectedFunnel)
    ) {
      setCardError(
          `Este contato já possui um card no funil.`
        );
        return;
      }

    try {
      setCreating(true);
      const clientName =
        availableClients.find((c) => c.id == newCardClientId)?.name ||
        String(newCardClientId);

      const response = await axios.post(`${CRM.CREATE_CARD}/${targetStage.id}`, {
        title: clientName,
        leadId: newCardClientId,
        responsibleId: newCardResponsible || null,
        priority: (targetStage.cards?.length || 0) + 1,
      });
      const createdCard =
        response?.data?.data || response?.data?.card || response?.data;

      if (createdCard) {
        setSelectedFunnel((prev) => {
          if (!prev) return prev;
          const stageList = (prev.stages || []).map((stage) => {
            if (String(stage.id) !== String(targetStage.id)) return stage;
            const currentCards = Array.isArray(stage.cards) ? stage.cards : [];
            return {
              ...stage,
              cards: [...currentCards, createdCard],
            };
          });
          return { ...prev, stages: stageList };
        });
      }

      await fetchFunnel(selectedFunnelId);

      setCardError("");
      setNewCardClientId("");
      setNewCardResponsible("");
      setNewCardModalOpen(false);
    } catch (err) {
      console.error("Erro ao criar card", err);
    } finally {
      setCreating(false);
    }
  };

  /* ====== Detalhes ====== */
  const fetchCardDetails = async (id) => {
    try {
      const res = await axios.get(`${CRM.GET_CARD}/${id}/details`);
      const historyRes = await axios.get(
        `${CRM.GET_CARD_HISTORY}/${id}/history`
      );
      const data = res.data.data;
      setSelectedTask({
        ...data,
        checklist: Array.isArray(data.checklist) ? data.checklist : [],
        customFields: fields,
      });
      setTaskHistory(historyRes.data.data);
    } catch (err) {
      console.error("Erro ao buscar detalhes do card", err);
    }
  };

  const refetchCard = async (cardId) => {
    try {
      const res = await axios.get(`${CRM.GET_CARD}/${cardId}/details`);
      const fresh = res?.data?.data;
      if (!fresh?.id) return;

      setSelectedFunnel((prev) => {
        if (!prev) return prev;

        // encontre a etapa e o índice atuais (antes do refetch)
        const srcStageIndex = prev.stages.findIndex((st) =>
          (st.cards || []).some((c) => String(c.id) === String(fresh.id))
        );
        const srcStage = srcStageIndex >= 0 ? prev.stages[srcStageIndex] : null;
        const srcIndex =
          srcStage?.cards?.findIndex(
            (c) => String(c.id) === String(fresh.id)
          ) ?? -1;

        // etapa alvo vinda do backend
        const tgtStageIndex = prev.stages.findIndex(
          (st) => String(st.id) === String(fresh.stageId)
        );

        // cópia rasa das etapas + arrays de cards
        const nextStages = prev.stages.map((st) => ({
          ...st,
          cards: [...(st.cards || [])],
        }));

        // caso 1: card continua na MESMA etapa -> substituir no MESMO índice
        if (
          srcStage &&
          tgtStageIndex === srcStageIndex &&
          srcIndex >= 0 &&
          tgtStageIndex >= 0
        ) {
          const keptPriority = Number.isFinite(
            +srcStage.cards[srcIndex]?.priority
          )
            ? +srcStage.cards[srcIndex].priority
            : srcIndex + 1;

          const updated = { ...fresh, priority: keptPriority };
          nextStages[srcStageIndex].cards[srcIndex] = updated;

          return { ...prev, stages: nextStages };
        }

        // caso 2: card mudou de etapa (backend alterou)
        if (srcStage && srcIndex >= 0) {
          // remove da origem, preservando as prioridades restantes
          nextStages[srcStageIndex].cards = nextStages[srcStageIndex].cards
            .filter((c) => String(c.id) !== String(fresh.id))
            .map((c, i) => ({ ...c, priority: i + 1 }));
        }

        if (tgtStageIndex >= 0) {
          const target = nextStages[tgtStageIndex];

          // posição-alvo: tente usar priority do backend (1-based). se inválida, use topo.
          const desiredPos = Number.isFinite(+fresh.priority)
            ? Math.max(0, Math.min(target.cards.length, +fresh.priority - 1))
            : 0;

          const withFresh = [...target.cards];
          withFresh.splice(desiredPos, 0, fresh);

          // reatribua priority coerente com a posição atual
          target.cards = withFresh.map((c, i) => ({ ...c, priority: i + 1 }));

          return { ...prev, stages: nextStages };
        }

        // se por algum motivo não achou etapa alvo, só retorna prev
        return prev;
      });
      setSelectedTask((prev) => {
        if (!prev || String(prev.id) !== String(fresh.id)) return prev;
        return {
          ...fresh,
          checklist: Array.isArray(fresh.checklist) ? fresh.checklist : [],
          customFields: fields,
        };
      });
      notifyAutomationMove(fresh);
      return fresh;
    } catch (err) {
      console.error("Erro ao refetch do card:", err);
    }
  };

  /* ====== Filters ====== */
  const getDueBucket = (dueDateStr) => {
    if (!dueDateStr) return "none";
    const str = String(dueDateStr).slice(0, 10);
    const [y, m, d] = str.split("-").map(Number);
    const due = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "overdue";
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "tomorrow";
    return "future";
  };

  const cardMatchesFilters = (card) => {
    // === Cliente ===
    const leadId = String(card?.lead?.id || "");

    if (filterClientIds && filterClientIds.length > 0) {
      const clientIdsStr = filterClientIds.map((id) => String(id));
      if (!clientIdsStr.includes(leadId)) return false;
    } else if (filterClientId && leadId !== String(filterClientId)) {
      // fallback pra versão antiga (single)
      return false;
    }

    // === Responsável ===
    if (
      filterResponsibleId &&
      String(card?.responsible?.id || "") !== String(filterResponsibleId)
    )
      return false;

    // Prazo, busca, tag... (mantém igual)
    if (filterDue) {
      const bucket = getDueBucket(card?.dueDate);
      if (bucket !== filterDue) return false;
    }
    if (filterSearch?.trim()) {
      const q = filterSearch.toLowerCase();
      const title = String(card?.title || "").toLowerCase();
      const customer = String(card?.lead?.name || "").toLowerCase();
      if (!title.includes(q) && !customer.includes(q)) return false;
    }
    if (filterTagId) {
      const cardTagId = String(card?.tag?.id || "");
      if (cardTagId !== String(filterTagId)) return false;
    }
    return true;
  };

  const openFilters = () => {
    setTmpFilterClientId(filterClientId);
    setTmpFilterClientIds(filterClientIds || []); // novo
    setTmpFilterResponsibleId(filterResponsibleId);
    setTmpFilterSearch(filterSearch);
    setTmpFilterDue(filterDue);
    setTmpFilterTagId(filterTagId);
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilterClientIds(tmpFilterClientIds || []);
    setFilterClientId(
      (tmpFilterClientIds && tmpFilterClientIds[0]) || tmpFilterClientId || ""
    );
    setFilterResponsibleId(tmpFilterResponsibleId);
    setFilterSearch(tmpFilterSearch);
    setFilterDue(tmpFilterDue);
    setFilterTagId(tmpFilterTagId);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilterClientId("");
    setFilterClientIds([]);
    setFilterResponsibleId("");
    setFilterSearch("");
    setFilterDue("");
    setFilterTagId("");
    setFiltersOpen(false);
  };

  const notifyAutomationMove = useCallback(
    (card) => {
      if (!card?.stage?.funnelId) return;
      if (!selectedFunnelId) return;
      if (String(card.stage.funnelId) === String(selectedFunnelId)) return;
      const targetFunnel = funnels.find(
        (f) => String(f.id) === String(card.stage?.funnelId)
      );
      const funnelName = targetFunnel?.name || "outro funil";
      const stageName = card.stage?.name || "nova etapa";
      setAutomationToast({
        id: Date.now(),
        message: `Automação moveu o card para "${stageName}" do funil ${funnelName}.`,
      });
      if (automationRefreshRef.current) {
        clearTimeout(automationRefreshRef.current);
      }
      automationRefreshRef.current = setTimeout(() => {
        fetchFunnel(selectedFunnelId);
        automationRefreshRef.current = null;
      }, 1000);
    },
    [funnels, selectedFunnelId]
  );

  const createFunnelFromTemplate = async (tpl) => {
    const payload = {
      name: tpl.name,
      visibility: "PUBLIC",
      stages: tpl.stages.map((name, i) => ({ name, order: i + 1 })),
      closingStageIndex: tpl.closingStageIndex,
    };

    const res = await axios.post(`${CRM.FUNNELS}`, payload);
    const created = res?.data?.data;

    await fetchFunnels();
    if (created?.id) {
      setSelectedFunnelId(created.id);
      await fetchFunnel(created.id);
    }
    return created;
  };

  const filteredByStage = useMemo(() => {
    const map = new Map();
    (selectedFunnel?.stages || []).forEach((st) => {
      const arr = (st.cards || []).filter(cardMatchesFilters);
      map.set(st.id, arr);
    });
    return map;
    // depende apenas de mudanças reais de stages/filtros
  }, [
    selectedFunnel?.stages, // muda quando você realmente atualiza o funil
    filterClientIds,
    filterResponsibleId,
    filterSearch,
    filterDue,
    filterTagId,
  ]);

  // dentro do componente pai (CRMTable), próximo de outras helpers:

  const renameStage = async (stageId, newName) => {
    // guarda o nome antigo pra rollback
    const prevName = selectedFunnel?.stages?.find(
      (s) => String(s.id) === String(stageId)
    )?.name;

    // otimista
    setSelectedFunnel((prev) => ({
      ...prev,
      stages: (prev?.stages || []).map((st) =>
        String(st.id) === String(stageId) ? { ...st, name: newName } : st
      ),
    }));

    try {
      await axios.put(`${CRM.UPDATE_STAGE}/${stageId}`, { name: newName });
    } catch (e) {
      // rollback
      setSelectedFunnel((prev) => ({
        ...prev,
        stages: (prev?.stages || []).map((st) =>
          String(st.id) === String(stageId) ? { ...st, name: prevName } : st
        ),
      }));
      console.error("Erro ao renomear etapa:", e);
      throw e;
    }
  };

  const [stageInspectorOpen, setStageInspectorOpen] = useState(false);
  const [stageInspectorStage, setStageInspectorStage] = useState(null);

  const openStageSettings = (stageId) => {
    const st = (selectedFunnel?.stages || []).find(
      (s) => String(s.id) === String(stageId)
    );
    if (!st) return;
    setStageInspectorStage({ ...st });
    setStageInspectorOpen(true);
  };

  const openFunnelConfig = () => {
    if (!selectedFunnel) return;
    // passe apenas o necessário (id, name, visibility, teamId, closingStageId, stages)
    const payload = {
      id: selectedFunnel.id,
      name: selectedFunnel.name,
      visibility: selectedFunnel.visibility || "PRIVATE",
      teamId: selectedFunnel.teamId || "",
      closingStageId: selectedFunnel.closingStageId || null,
      stages: selectedFunnel.stages || [],
      ownerUserId: selectedFunnel.ownerUserId || null,
    };
    setFunnelEditorData(payload);
    setFunnelEditorOpen(true);
  };

  // aplica patch no estado local (somente na etapa que está aberta)
  const patchStageInState = (patch) => {
    if (!stageInspectorStage?.id) return;
    const id = stageInspectorStage.id;
    setSelectedFunnel((prev) => ({
      ...prev,
      stages: (prev?.stages || []).map((s) =>
        String(s.id) === String(id) ? { ...s, ...patch } : s
      ),
    }));
    setStageInspectorStage((prev) => ({ ...(prev || {}), ...patch }));
  };

  // rename no estado local
  const renameStageInState = (newName) => patchStageInState({ name: newName });

  // persistência imediata no backend a cada mudança (opcional: debouncer)
  const persistStagePatch = async (patch) => {
    if (!stageInspectorStage?.id) return;
    try {
      await axios.put(`${CRM.UPDATE_STAGE}/${stageInspectorStage.id}`, patch);
      // ok
    } catch (e) {
      console.error("Erro ao salvar etapa:", e);
    }
  };

  // wrappers que aplicam local salvam no backend
  const handleInspectorPatch = (patch) => {
    patchStageInState(patch);
    persistStagePatch(patch);
  };
  const handleInspectorRename = (newName) => {
    renameStageInState(newName);
    persistStagePatch({ name: newName });
  };

  /* ====== Render ====== */
  if (loading) return <PageLoader />;
  if (!loading && funnels.length === 0) {
    return (
      <>
        <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
          <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-rose-500/15 to-amber-400/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-gradient-to-tr from-red-500/15 to-cyan-400/15 blur-3xl" />

          <div className="p-10 md:p-12 text-center relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-3 py-1 text-xs font-medium text-gray-600 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              Comece seu primeiro funil
            </span>

            <div className="mx-auto mt-6 h-28 w-28 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 p-[2px] shadow-lg">
              <div className="flex h-full w-full items-center justify-center rounded-2xl bg-white">
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 24 24"
                  className="text-red-600"
                >
                  <path fill="currentColor" d="M3 4h18v2l-7 7v4l-4 2v-6L3 6z" />
                </svg>
              </div>
            </div>

            <h3 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
              Nenhum funil por aqui… ainda
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-sm md:text-base text-gray-600">
              Crie um funil para organizar etapas, distribuir cards e acelerar
              suas vendas.
            </p>

            <div className="mx-auto mt-6 max-w-2xl grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
              {[
                {
                  t: "Estruture etapas",
                  d: "Defina o fluxo: Prospecção → Contato → Proposta → Fechamento.",
                },
                {
                  t: "Atribua responsáveis",
                  d: "Distribua cards e acompanhe o avanço por pessoa.",
                },
                {
                  t: "Automatize ações",
                  d: "Lembretes, follow-ups e tarefas do dia na sua agenda.",
                },
              ].map((it, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white/60 p-4"
                >
                  <div className="mb-1 text-sm font-semibold text-gray-900">
                    {it.t}
                  </div>
                  <div className="text-xs text-gray-600">{it.d}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                size="lg"
                onClick={() => setTemplateModalOpen(true)}
                className="bg-primary"
              >
                <span className="inline-flex items-center gap-2">
                  Criar novo funil
                </span>
              </Button>
            </div>
          </div>
        </div>
        <FunnelTemplateModal
          show={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          onCreateFromTemplate={createFunnelFromTemplate}
        />
      </>
    );
  }
  if (!selectedFunnel) return <PageLoader />;

  const dropAnimation = {
    duration: 200,
    easing: "cubic-bezier(.2,.8,.2,1)",
  };

  return (
    <>
      {/* Botão para reexibir header quando oculto */}
      {headerHidden && (
        <button
          onClick={() => setHeaderHidden(false)}
          className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-white"
          title="Mostrar barra"
        >
          <HiEye /> Mostrar barra
        </button>
      )}

      {!headerHidden && (
        <div className="relative rounded-xl border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm">
          {/* Auras */}
          <div className="pointer-events-none absolute -top-16 -left-20 h-44 w-44 rounded-full bg-gradient-to-br from-rose-400/10 via-fuchsia-400/10 to-indigo-400/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-16 -right-20 h-44 w-44 rounded-full bg-gradient-to-tl from-cyan-400/10 via-sky-400/10 to-teal-400/10 blur-2xl" />

          {/* Header em linha única (scroll horizontal se precisar) */}
          <div className="relative z-10 px-3 md:px-4 py-2">
            <div className="overflow-x-auto whitespace-nowrap no-scrollbar">
              <div className="inline-flex items-center w-full gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/board")} // troque por navigate(-1) se preferir histórico
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-300 px-2.5 py-1 text-[12px] font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  title="Voltar para Boards"
                >
                  <FaArrowLeftLong />
                  <span className="hidden md:block">Voltar</span>
                </button>
                {/* Chip do funil */}
                {/* Chip do funil + dropdown de seleção */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsFunnelPickerOpen((prev) => !prev)}
                    className="inline-flex items-center gap-2 rounded-full border border-[#E35412] bg-[#E35412]/20 px-2.5 py-1.5 shadow-sm hover:bg-[#E35412]/30 transition-colors"
                    title="Alterar funil"
                  >
                    <span className="text-[13px] font-semibold text-gray-900 leading-none flex items-center">
                      <span className="hidden md:block">Funil: </span>
                      <span className="ml-1 font-medium text-gray-700 truncate max-w-[160px] sm:max-w-[220px]">
                        {selectedFunnel?.name || "Selecione um funil"}
                      </span>
                      <FaExchangeAlt className="ml-3 opacity-80" />
                    </span>
                  </button>

                  <Modal
                    show={isFunnelPickerOpen}
                    onClose={() => setIsFunnelPickerOpen(false)}
                  >
                    <Modal.Header>
                      <div className="text-base font-semibold text-gray-800">
                        Trocar Funil
                      </div>
                    </Modal.Header>
                    <Modal.Body className="!overflow-visible">
                      <div className="text-sm font-semibold">
                        Escolha o funil:
                      </div>
                      <div
                        className="overflow-x-scroll pr-2"
                        style={{ maxHeight: "250px" }}
                      >
                        <ul className="flex flex-col mt-2 py-1 border border-gray-200 rounded-md">
                          {funnels?.map((funnel) => {
                            const isActive =
                              selectedFunnel && funnel.id === selectedFunnel.id;
                            return (
                              <li key={funnel.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsFunnelPickerOpen(false);
                                    setSelectedFunnelId(funnel.id);

                                    navigate(`/crm?f=${funnel.id}`);
                                  }}
                                  className={[
                                    "w-full text-left px-3 py-2 text-[13px] flex items-center justify-between gap-2 hover:bg-gray-50",
                                    isActive
                                      ? "bg-orange-50 text-orange-800"
                                      : "text-gray-800",
                                  ].join(" ")}
                                >
                                  <span className="truncate">
                                    {funnel.name}
                                  </span>
                                  {isActive && (
                                    <span className="text-[11px] rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">
                                      atual
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </Modal.Body>
                  </Modal>
                </div>

                {/* Botão Filtros */}
                <Button
                  onClick={openFilters}
                  color="gray"
                  size="xs"
                  className="!h-7 !rounded-full !border-gray-200 !bg-white hover:!bg-gray-50 shadow-sm"
                  title="Abrir filtros"
                >
                  <div className="flex gap-1.5 items-center text-[12px]">
                    <FaFilter />
                    <span>Filtros</span>
                  </div>
                </Button>

                {/* Alternador de visualização */}
                <div className="inline-flex items-center gap-1 ml-1 rounded-full border border-gray-200 bg-white p-1 shadow-sm">
                  <button
                    onClick={() => setViewMode("kanban")}
                    title="Kanban"
                    className={[
                      "px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1",
                      viewMode === "kanban"
                        ? "bg-gray-900 text-white"
                        : "text-gray-700 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <HiViewGrid />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    title="Lista"
                    className={[
                      "px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1",
                      viewMode === "list"
                        ? "bg-gray-900 text-white"
                        : "text-gray-700 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <HiViewList />
                  </button>
                  <button
                    onClick={() => setViewMode("responsible")}
                    title="Por responsável"
                    className={[
                      "px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1",
                      viewMode === "responsible"
                        ? "bg-gray-900 text-white"
                        : "text-gray-700 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <HiUser />
                  </button>
                </div>

                {/* Quick filter do cliente */}
                <div className="hidden sm:flex items-center min-w-[160px] flex-1">
                  <ClientQuickFilter
                    options={(availableClients || []).map((c) => ({
                      value: String(c.id),
                      label: c.name,
                    }))}
                    // usamos filterClientId só como "texto/digitação" atual
                    value={filterClientId}
                    onChange={(id) => {
                      const idStr = id ? String(id) : "";

                      // Se limpou via clear, não mexe nos filtros (chips cuidam da limpeza)
                      if (!idStr) {
                        setFilterClientId("");
                        return;
                      }

                      // acumula e, depois de aplicar, limpa o campo para próxima busca
                      setFilterClientIds((prev) => {
                        if (prev.includes(idStr)) return prev;
                        return [...prev, idStr];
                      });
                      setFilterClientId("");
                    }}
                    onClear={() => {
                      // limpa tudo: input + filtros
                      setFilterClientId("");
                      setFilterClientIds([]);
                    }}
                    isLoading={false}
                    className="ml-2 w-full"
                  />
                </div>

                {canManageFunnels && (
                  <Button
                    ref={configBtnRef}
                    onClick={toggleConfigMenu}
                    size="xs"
                    className="!h-7 !rounded-full !border-gray-200 hover:!bg-gray-500 shadow-sm bg-gray-400"
                    title="Configurações do funil"
                  >
                    <div className="flex gap-1.5 items-center text-[12px]">
                      <HiCog6Tooth className="h-4 w-4" />
                      <span className="hidden md:block">Configurações</span>
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chips de filtros ativos */}
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {filterClientIds && filterClientIds.length > 0 && (
          <span className="inline-flex items-center gap-1.5 bg-white px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            <span className="mr-1">Cliente(s):</span>

            <span className="flex flex-wrap gap-1">
              {filterClientIds.map((clientId) => {
                const cli =
                  availableClients.find(
                    (c) => String(c.id) === String(clientId)
                  ) || null;

                return (
                  <span
                    key={clientId}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 border border-gray-200"
                  >
                    {cli?.name || clientId}
                    <button
                      type="button"
                      className="ml-0.5 text-gray-500 hover:text-gray-800"
                      onClick={() =>
                        setFilterClientIds((prev) =>
                          prev.filter((id) => String(id) !== String(clientId))
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}

              {/* limpar todos, opcional */}
              <button
                type="button"
                className="ml-1 text-[10px] text-red-500 hover:text-red-700"
                onClick={() => setFilterClientIds([])}
              >
                limpar
              </button>
            </span>
          </span>
        )}

        {filterResponsibleId && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Resp.:{" "}
            {availableUsers.find(
              (u) => String(u.id) === String(filterResponsibleId)
            )?.name || filterResponsibleId}
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterResponsibleId("")}
            >
              ×
            </button>
          </span>
        )}
        {filterDue && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Prazo:{" "}
            {{
              overdue: "Vencido",
              today: "Hoje",
              tomorrow: "Amanhã",
              future: "Futuro",
              none: "Sem prazo",
            }[filterDue] || filterDue}
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterDue("")}
            >
              ×
            </button>
          </span>
        )}
        {filterSearch && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Busca: “{filterSearch}”
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterSearch("")}
            >
              ×
            </button>
          </span>
        )}

        {filterTagId && (
          <span className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-0.5 rounded-full text-[11px] border border-gray-200">
            Tag:{" "}
            {tagOptions.find((t) => String(t.id) === String(filterTagId))
              ?.name || filterTagId}
            <button
              className="ml-0.5 text-gray-500 hover:text-gray-800"
              onClick={() => setFilterTagId("")}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {/* ====== ÁREA PRINCIPAL (3 views) ====== */}
      {viewMode === "kanban" && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          modifiers={[restrictToWindowEdges]}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="mt-4 h-[calc(100vh-190px)] rounded-xl bg-gray-200 overflow-x-auto overflow-y-hidden">
            <div
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "0 0",
                width: `${(1 / zoom) * 100}%`,
                height: `calc((100vh - 190px) / ${zoom})`,
              }}
            >
              <div className="inline-flex gap-4 pb-4 h-full p-3">
                {selectedFunnel?.stages?.map((stage) => (
                  <KanbanColumn
                    key={stage.id}
                    title={stage.name}
                    columnId={stage.id}
                    color={stage.color}
                    tasks={filteredByStage.get(stage.id) || []}
                    onCardClick={(task) => openCardById(task?.id)}
                    isClosing={
                      String(stage.id) ===
                      String(selectedFunnel?.closingStageId || "")
                    }
                    onOpenStageSettings={openStageSettings}
                    onStageRename={renameStage}
                    user={user}
                    canManageFunnels={canManageFunnels}
                  />
                ))}
              </div>
            </div>
          </div>

          <DragOverlay dropAnimation={dropAnimation}>
            {activeCard ? (
              <div className="w-[300px]">
                <SortableItem
                  task={activeCard}
                  onClick={() => {}}
                  dimmed={false}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {viewMode === "list" && (
        <ListView
          funnel={selectedFunnel}
          cardMatchesFilters={cardMatchesFilters}
          onOpenCard={(id) => openCardById(id)}
        />
      )}

      {viewMode === "responsible" && (
        <ResponsibleView
          funnel={selectedFunnel}
          cardMatchesFilters={cardMatchesFilters}
          onOpenCard={(id) => openCardById(id)}
        />
      )}

      {/* Detalhes do card */}
      {selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={closeCardModal}
          updateTask={updateTask}
          history={taskHistory}
          setHistory={setTaskHistory}
          setSelectedTask={setSelectedTask}
          onRefetchCard={refetchCard}
          stages={selectedFunnel?.stages || []}
          onMoveStage={async (cardId, targetStageId) => {
            const sourceStage = selectedFunnel?.stages?.find((s) =>
              s.cards?.some((c) => c.id === cardId)
            );
            const targetStage = selectedFunnel?.stages?.find(
              (s) => s.id === targetStageId
            );
            if (
              !sourceStage ||
              !targetStage ||
              sourceStage.id === targetStage.id
            )
              return;

            const requiredFields = getRequiredFieldsForStage(targetStage.id);
            if (requiredFields.length > 0) {
              const movingCard =
                targetStage.cards.find((c) => c.id === cardId) ||
                sourceStage.cards.find((c) => c.id === cardId);

              const initialValues = buildCardCustomFieldValuesMap(
                movingCard,
                requiredFields
              );

              setPendingStageChange({
                card: movingCard,
                sourceStageId: sourceStage.id,
                targetStageId: targetStage.id,
                requiredFields,
              });
              setRequiredFieldValues(initialValues);
              setRequiredError("");
              return true;
            }

            const moving = sourceStage.cards.find((c) => c.id === cardId);
            const updatedSource = sourceStage.cards
              .filter((c) => c.id !== cardId)
              .map((c, i) => ({
                ...c,
                priority: i + 1,
                stageId: sourceStage.id,
              }));

            const updatedTarget = [
              { ...moving, priority: 1, stageId: targetStage.id },
              ...targetStage.cards.map((c, i) => ({
                ...c,
                priority: i + 2,
                stageId: targetStage.id,
              })),
            ];

            updateStageCards(sourceStage.id, updatedSource);
            updateStageCards(targetStage.id, updatedTarget);

            try {
              await Promise.all([
                ...updatedSource.map((card) =>
                  updateCardBackend(card.id, {
                    stageId: sourceStage.id,
                    priority: card.priority,
                  })
                ),
                ...updatedTarget.map((card) =>
                  updateCardBackend(card.id, {
                    stageId: targetStage.id,
                    priority: card.priority,
                  })
                ),
              ]);
              await refetchCard(cardId);
            } catch (e) {
              console.error("Erro ao mover card via modal:", e);
            }
            return false;
          }}
          onCardDeleted={(cardId) => {
            setSelectedFunnel((prev) => {
              if (!prev) return prev;
              const nextStages = prev.stages.map((st) => {
                const had = (st.cards || []).some(
                  (c) => String(c.id) === String(cardId)
                );
                if (!had) return st;
                const newCards = (st.cards || [])
                  .filter((c) => String(c.id) !== String(cardId))
                  .map((c, i) => ({ ...c, priority: i + 1 }));
                return { ...st, cards: newCards };
              });
              return { ...prev, stages: nextStages };
            });
            setSelectedTask(null);
          }}
        />
      )}

      {/* Modal de campos obrigatórios ao mover de etapa */}
      {pendingStageChange && (
        <Modal
          show={true}
          size="2xl"
          onClose={handleCancelRequiredModal}
          className="!z-[9999]"
          style={{ zIndex: 9999 }}
        >
          <Modal.Header className="flex items-center">
            <div className="text-sm">
              Campos obrigatórios para avançar para:{" "}
              {
                selectedFunnel?.stages?.find(
                  (st) =>
                    String(st.id) === String(pendingStageChange.targetStageId)
                )?.name
              }
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Antes de mover o card{" "}
                <strong>
                  {pendingStageChange.card?.lead?.name ||
                    pendingStageChange.card?.title ||
                    "sem título"}
                </strong>{" "}
                para essa etapa, preencha os campos abaixo:
              </p>

              {requiredError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {requiredError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                {pendingStageChange.requiredFields.map((field) => {
                  const rawType = (field.type || "text").toLowerCase();
                  const type =
                    rawType === "single_select"
                      ? "select"
                      : rawType === "multi_select"
                      ? "multiselect"
                      : rawType;

                  const key = resolveFieldKey(field);
                  const options = Array.isArray(field.options)
                    ? field.options
                    : [];
                  const value =
                    type === "multiselect"
                      ? requiredFieldValues[key] || []
                      : requiredFieldValues[key] ?? "";

                  const labelNode = (
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {field.label || field.name || field.key || "Campo"}
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                  );

                  // TEXTAREA
                  if (type === "textarea") {
                    return (
                      <div key={field.id} className="md:col-span-2">
                        {labelNode}
                        <textarea
                          rows={3}
                          className="w-full rounded-lg border border-gray-300 text-sm text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-gray-300"
                          value={value || ""}
                          onChange={(e) =>
                            handleChangeRequiredField(field, e.target.value)
                          }
                          placeholder={field.placeholder || ""}
                        />
                      </div>
                    );
                  }

                  if (type === "select") {
                    return (
                      <div key={field.id}>
                        {labelNode}
                        <Select
                          value={value || ""}
                          onChange={(e) =>
                            handleChangeRequiredField(field, e.target.value)
                          }
                          required
                        >
                          <option value="">Selecione...</option>
                          {options.map((opt) => {
                            // agora prioriza o ID sempre
                            if (typeof opt === "string") {
                              return (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              );
                            }
                            const val = opt.value ?? opt.id ?? opt.label;
                            const lab =
                              opt.label ?? opt.value ?? String(opt.id);

                            return (
                              <option key={val} value={val}>
                                {lab}
                              </option>
                            );
                          })}
                        </Select>
                      </div>
                    );
                  }

                  // MULTISELECT (multi_select)
                  if (type === "multiselect") {
                    const selectedValues = Array.isArray(value) ? value : [];

                    return (
                      <div key={field.id}>
                        {labelNode}
                        <div className="flex flex-wrap gap-1.5">
                          {options.map((opt) => {
                            if (typeof opt === "string") {
                              const checked = selectedValues.includes(opt);
                              return (
                                <button
                                  type="button"
                                  key={opt}
                                  onClick={() =>
                                    handleToggleMultiRequiredField(field, opt)
                                  }
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] ${
                                    checked
                                      ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                                      : "bg-white border-gray-200 text-gray-600"
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-2 w-2 rounded-full ${
                                      checked ? "bg-indigo-600" : "bg-gray-300"
                                    }`}
                                  />
                                  <span>{opt}</span>
                                </button>
                              );
                            }

                            const val = opt.id ?? opt.value ?? opt.label;
                            const lab =
                              opt.label ?? opt.value ?? String(opt.id);
                            const checked = selectedValues.includes(val);

                            return (
                              <button
                                type="button"
                                key={val}
                                onClick={() =>
                                  handleToggleMultiRequiredField(field, val)
                                }
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] ${
                                  checked
                                    ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                                    : "bg-white border-gray-200 text-gray-600"
                                }`}
                              >
                                <span
                                  className={`inline-block h-2 w-2 rounded-full ${
                                    checked ? "bg-indigo-600" : "bg-gray-300"
                                  }`}
                                />
                                <span>{lab}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  // BOOLEAN
                  if (type === "boolean") {
                    const boolVal = value === true || value === "true";
                    return (
                      <div key={field.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={boolVal}
                          onChange={(e) =>
                            handleChangeRequiredField(field, e.target.checked)
                          }
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                        />
                        <span className="text-xs font-medium text-gray-700">
                          {field.label || field.name || field.key || "Campo"}
                          <span className="text-red-500 ml-0.5">*</span>
                        </span>
                      </div>
                    );
                  }

                  // DATE
                  if (type === "date") {
                    return (
                      <div key={field.id}>
                        {labelNode}
                        <input
                          type="date"
                          className="w-full rounded-lg border border-gray-300 text-sm text-gray-800 focus:border-gray-400 focus:ring-gray-300"
                          value={value || ""}
                          onChange={(e) =>
                            handleChangeRequiredField(field, e.target.value)
                          }
                        />
                      </div>
                    );
                  }

                  // DATETIME
                  if (type === "datetime") {
                    return (
                      <div key={field.id}>
                        {labelNode}
                        <input
                          type="datetime-local"
                          className="w-full rounded-lg border border-gray-300 text-sm text-gray-800 focus:border-gray-400 focus:ring-gray-300"
                          value={value || ""}
                          onChange={(e) =>
                            handleChangeRequiredField(field, e.target.value)
                          }
                        />
                      </div>
                    );
                  }

                  // NUMBER / CURRENCY
                  if (type === "number" || type === "currency") {
                    return (
                      <div key={field.id}>
                        {labelNode}
                        <input
                          type="number"
                          className="w-full rounded-lg border border-gray-300 text-sm text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-gray-300"
                          value={value || ""}
                          onChange={(e) =>
                            handleChangeRequiredField(field, e.target.value)
                          }
                          placeholder={field.placeholder || ""}
                        />
                      </div>
                    );
                  }

                  // DEFAULT: TEXT
                  return (
                    <div key={field.id}>
                      {labelNode}
                      <input
                        type="text"
                        className="w-full rounded-lg border border-gray-300 text-sm text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-gray-300"
                        value={value || ""}
                        onChange={(e) =>
                          handleChangeRequiredField(field, e.target.value)
                        }
                        placeholder={field.placeholder || ""}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex flex-col md:flex-row items-center gap-2 justify-center w-full">
              <Button
                onClick={handleConfirmStageChange}
                isProcessing={savingRequiredFields}
              >
                Salvar e mover etapa
              </Button>
              <Button
                color="light"
                onClick={handleCancelRequiredModal}
                disabled={savingRequiredFields}
                className="px-5"
              >
                Cancelar
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {/* Botão flutuante */}
      <FloatingAddButton onClick={() => setNewCardModalOpen(true)} />

      {/* Zoom só no Kanban */}
      {viewMode === "kanban" && (
        <FloatingZoomControls
          value={zoom}
          onIn={zoomIn}
          onOut={zoomOut}
          onReset={zoomReset}
          min={ZMIN}
          max={ZMAX}
        />
      )}

      {/* Modal: Criar Card */}
      <Modal show={newCardModalOpen} onClose={closeNewCardModal}>
        <Modal.Header>
          <div className="text-lg font-semibold text-gray-800">
            Criar novo card
          </div>
        </Modal.Header>
        <Modal.Body className="!overflow-visible">
          <div className="space-y-6">
            {publicFormLink && (
              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-3 text-sm text-orange-900 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <HiLink className="h-5 w-5 text-orange-600" />
                    <div className="text-sm font-semibold text-orange-900">
                      Formulário público ativo
                    </div>
                  </div>
                  <Button
                    size="xs"
                    color="orange"
                    disabled={publicFormLoading}
                    onClick={copyPublicFormLink}
                    className="flex items-center gap-1"
                  >
                    {publicFormLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      "Copiar link"
                    )}
                  </Button>
                </div>
                <p className="mt-2 break-all text-xs font-medium text-orange-900">
                  {publicFormLink}
                </p>
                {copyPublicFormFeedback && (
                  <p className="mt-1 text-[11px] text-orange-700">
                    {copyPublicFormFeedback}
                  </p>
                )}
              </div>
            )}
            <div>
              <AutoCompleteInput
                label="Cliente *"
                name="newCardClient"
                options={(availableClients || []).map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                value={newCardClientId}
                onChange={(opt) => {
                  setNewCardClientId(opt?.value || "");
                  setCardError("");
                }}
              />

              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setQuickCustomerOpen(true)}
                  className="text-sm text-blue-700 hover:underline"
                >
                  Não encontrou? Cadastrar novo contato
                </button>
              </div>

              <QuickCreateCustomer
                show={quickCustomerOpen}
                onClose={() => setQuickCustomerOpen(false)}
                onCreated={(created) => {
                  setAvailableClients((prev) => [created, ...prev]);
                  setNewCardClientId(created.id);
                }}
              />
            </div>

            {/* Etapa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etapa destino *
              </label>
              <Select
                value={newCardStageId}
                onChange={(e) => {
                  setNewCardStageId(e.target.value);
                  setCardError("");
                }}
              >
                {(selectedFunnel?.stages || []).map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável do card
              </label>
              <Select
                value={newCardResponsible}
                onChange={(e) => setNewCardResponsible(e.target.value)}
              >
                <option value="">Não atribuído</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-rose-600 min-h-[1rem] text-left">
            {cardError}
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              onClick={handleCreateCard}
              disabled={!newCardClientId || !newCardStageId || creating}
              className="w-full sm:w-auto"
            >
              {creating ? "Criando..." : "Criar Card"}
            </Button>
            <Button
              color="gray"
              onClick={closeNewCardModal}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal: Filtros */}
      <Modal show={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Modal.Header>
          <div className="text-lg font-semibold text-gray-800">Filtros</div>
        </Modal.Header>
        <Modal.Body className="!overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Clientes (multi) */}
            <div className="md:col-span-2">
              <div className="flex gap-2 ">
                <div className="flex-1">
                  <Label>Clientes</Label>
                  <AutoCompleteInput
                    label={"Clientes"}
                    name="filterClients"
                    options={(availableClients || []).map((c) => ({
                      value: String(c.id),
                      label: c.name,
                    }))}
                    value={tmpFilterClientId}
                    onChange={(opt) => setTmpFilterClientId(opt?.value || "")}
                  />
                </div>

                <Button
                  type="button"
                  color="gray"
                  className="self-start mt-7"
                  onClick={() => {
                    if (!tmpFilterClientId) return;
                    const idStr = String(tmpFilterClientId);
                    setTmpFilterClientIds((prev) =>
                      prev.includes(idStr) ? prev : [...prev, idStr]
                    );
                    // limpa o campo pra facilitar adicionar o próximo
                    setTmpFilterClientId("");
                  }}
                >
                  Adicionar
                </Button>
              </div>

              {/* Chips dos clientes selecionados */}
              {tmpFilterClientIds.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {tmpFilterClientIds.map((id) => {
                    const cli = (availableClients || []).find(
                      (c) => String(c.id) === String(id)
                    );
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800 border border-gray-200"
                      >
                        {cli?.name || id}
                        <button
                          type="button"
                          className="text-gray-500 hover:text-gray-800"
                          onClick={() =>
                            setTmpFilterClientIds((prev) =>
                              prev.filter((x) => String(x) !== String(id))
                            )
                          }
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}

                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline ml-1"
                    onClick={() => setTmpFilterClientIds([])}
                  >
                    Limpar todos
                  </button>
                </div>
              )}
            </div>

            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável
              </label>
              <Select
                value={tmpFilterResponsibleId}
                onChange={(e) => setTmpFilterResponsibleId(e.target.value)}
              >
                <option value="">Todos</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Prazo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prazo
              </label>
              <Select
                value={tmpFilterDue}
                onChange={(e) => setTmpFilterDue(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="overdue">Vencido</option>
                <option value="today">Hoje</option>
                <option value="tomorrow">Amanhã</option>
                <option value="future">Futuro</option>
                <option value="none">Sem prazo</option>
              </Select>
            </div>

            {/* Tag */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag
              </label>
              <Select
                value={tmpFilterTagId}
                onChange={(e) => setTmpFilterTagId(e.target.value)}
              >
                <option value="">Todas</option>
                {tagOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="flex justify-between">
          <Button color="gray" onClick={clearFilters}>
            Limpar
          </Button>
          <div className="flex gap-2">
            <Button color="gray" onClick={() => setFiltersOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={applyFilters}>Aplicar</Button>
          </div>
        </Modal.Footer>
      </Modal>

      <StageInspectorDrawer
        open={stageInspectorOpen}
        stage={stageInspectorStage}
        onClose={() => setStageInspectorOpen(false)}
        onPatch={handleInspectorPatch} // persiste campo a campo
        onRename={handleInspectorRename}
      />

      <FunnelEditorModal
        open={funnelEditorOpen}
        onClose={() => setFunnelEditorOpen(false)}
        initialFunnel={funnelEditorData}
        onSaved={async (saved) => {
          // após salvar, refaça o fetch do funil atual para refletir mudanças
          await fetchFunnel(selectedFunnelId);
        }}
      />
      {canManageFunnels && configMenuOpen && (
        <div
          ref={configMenuRef}
          className="fixed z-50"
          style={{
            top: configMenuPos.top,
            left: configMenuPos.left,
          }}
        >
          <div className="rounded-xl border border-gray-200 bg-white shadow-xl min-w-[220px] py-2">
            <button
              type="button"
              onClick={() => {
                setConfigMenuOpen(false);
                openFunnelConfig();
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
            >
              <HiCog6Tooth className="h-4 w-4 text-gray-500" />
              <span>Configurar funil</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setConfigMenuOpen(false);
                if (!selectedFunnelId) return;
                navigate(
                  `/crm/automations?f=${encodeURIComponent(selectedFunnelId)}`
                );
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
            >
              <HiSparkles className="h-4 w-4 text-gray-500" />
              <span>Configurar automações</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setConfigMenuOpen(false);
                if (!selectedFunnelId) return;
                navigate(
                  `/crm/funnels/${encodeURIComponent(
                    selectedFunnelId
                  )}/public-form`
                );
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
            >
              <FaWpforms className="h-4 w-4 text-gray-500" />
              <span>Configurar formulário público</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setConfigMenuOpen(false);
                openCustomFieldsConfig();
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
            >
              <HiPencilSquare className="h-4 w-4 text-gray-500" />
              <span>Configurar campos customizados</span>
            </button>
          </div>
        </div>
      )}
      <AnimatedToast
        open={Boolean(automationToast)}
        message={automationToast?.message}
        onClose={() => setAutomationToast(null)}
      />
    </>
  );
}
